import { makeAutoObservable, runInAction } from "mobx"
import {
    expandHomePathReference,
    hasImageExtension,
    hasPdfExtension,
    isAbsolutePathReference,
    isHomeRelativePathReference,
    isPathInsideDirectory,
} from "../../components/utils/pathReference"
import { getFileName, getRelativePath, joinPath, splitPath } from "../../components/utils/paths"
import { type DescribePathResponse, type FileLocation, type FsWorkspaceContext, type PathEntry, filesApi } from "../../nestAPIs/files"
import { getPathSeparator } from "../../nestAPIs/platform"
import type { NestFileBytes } from "../../nest/fileBytes"

const MAX_FILE_READ_SIZE = 5 * 1024 * 1024 // 5MB
const MAX_OPEN_TABS = 8
const FILE_REFERENCE_SEARCH_LIMIT = 12

// Infrastructure directories to always hide in tree view (not searchable either via gitignore)
const HIDDEN_INFRA_DIRS = new Set([".git"])

function normalizePathForMatch(path: string): string {
    return path.replace(/\\/g, "/").replace(/^\.\//, "").replace(/\/+/g, "/").toLowerCase()
}

function normalizeFileReferenceInput(referencePath: string): string {
    const trimmed = referencePath.trim()
    const withoutMention = trimmed.startsWith("@") ? trimmed.slice(1).trimStart() : trimmed
    return isHomeRelativePathReference(withoutMention) ? expandHomePathReference(withoutMention) : withoutMention
}

function chooseBestFuzzyReferenceMatch(results: string[], query: string): string | null {
    if (results.length === 0) return null

    const normalizedQuery = normalizePathForMatch(query)
    const exact = results.find((result) => normalizePathForMatch(result) === normalizedQuery)
    if (exact) return exact

    const suffix = results.find((result) => normalizePathForMatch(result).endsWith(`/${normalizedQuery}`))
    if (suffix) return suffix

    return results[0] ?? null
}

export interface TreeNode {
    path: string
    name: string
    isDir: boolean
    depth: number
    isExpanded: boolean
    isLoading: boolean
}

export interface OpenTab {
    path: string
    name: string
    external: boolean
}

export class FileBrowserManager {
    // Navigation state
    workingDir = ""

    // Tree state - expanded directories and their cached contents
    expandedPaths: Set<string> = new Set()
    directoryContents: Map<string, PathEntry[]> = new Map()
    loadingPaths: Set<string> = new Set()

    // Sidebar visibility
    sidebarOpen = false

    // Search state
    searchQuery = ""
    searchResults: string[] = []
    untrackedSearchResults: Set<string> = new Set()
    searchLoading = false

    // Open tabs in stable display order
    openTabs: OpenTab[] = []

    // Currently active file
    activeFile: string | null = null
    activeFileData: Extract<DescribePathResponse, { type: "file" }> | null = null
    activeLine: number | null = null
    fileLoading = false
    fileError: string | null = null

    // Selection in tree
    selectedPath: string | null = null

    // Settings
    showHidden = true // Show dotfiles like .env, .gitignore by default

    private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null
    private fsContext: FsWorkspaceContext | null = null
    private directoryRevision = 0

    constructor(context?: FsWorkspaceContext | null) {
        this.fsContext = context ?? null
        makeAutoObservable(this)
    }

    /**
     * Files inside the working directory read through the workspace jail.
     * Anything else — another checkout, ~/.claude, /tmp — reads through the
     * node-scoped host surface, which is what makes out-of-repo references
     * openable at all.
     */
    fileLocation(path: string): FileLocation {
        if (!isPathInsideDirectory(this.workingDir, path)) return { kind: "host", path }
        if (!this.fsContext) throw new Error("Nest filesystem calls require workspace context")
        return { kind: "workspace", path, context: this.fsContext }
    }

    isExternalPath(path: string): boolean {
        return !isPathInsideDirectory(this.workingDir, path)
    }

    async readFileBytes(path: string, options: { signal?: AbortSignal } = {}): Promise<NestFileBytes> {
        return filesApi.readFileBytes(this.fileLocation(path), options)
    }

    async describeWorkspaceFileReference(referencePath: string): Promise<Extract<DescribePathResponse, { type: "file" }> | null> {
        const path = this.resolveFileReferencePath(referencePath)
        if (!path || this.isExternalPath(path)) return null

        const result = await this.describeAt(path)
        return result.type === "file" ? result : null
    }

    async readWorkspaceFileReferenceBytes(referencePath: string, options: { signal?: AbortSignal } = {}): Promise<NestFileBytes> {
        const path = this.resolveFileReferencePath(referencePath)
        if (!path || this.isExternalPath(path)) {
            throw new Error("Inline images must be inside the task working directory")
        }
        return this.readFileBytes(path, options)
    }

    private withContext<T extends object>(params: T): T & { context?: FsWorkspaceContext } {
        return this.fsContext ? { ...params, context: this.fsContext } : params
    }

    setWorkingDir(path: string): void {
        this.setDirectory(path, this.fsContext)
    }

    setDirectory(path: string, context: FsWorkspaceContext | null): void {
        const contextChanged =
            this.fsContext?.workspaceId !== context?.workspaceId ||
            this.fsContext?.workspaceRoot !== context?.workspaceRoot ||
            this.fsContext?.worktreeId !== context?.worktreeId
        if (this.workingDir === path && !contextChanged) return

        this.directoryRevision += 1
        this.fsContext = context
        this.workingDir = path
        this.expandedPaths.clear()
        this.directoryContents.clear()
        this.loadingPaths.clear()
        this.searchQuery = ""
        this.searchResults = []
        this.untrackedSearchResults.clear()
        this.searchLoading = false
        this.openTabs = []
        this.activeFile = null
        this.activeFileData = null
        this.activeLine = null
        this.selectedPath = null
        this.fileLoading = false
        this.fileError = null
        if (this.searchDebounceTimer) {
            clearTimeout(this.searchDebounceTimer)
            this.searchDebounceTimer = null
        }
        if (path) {
            this.expandedPaths.add(path)
            void this.loadDirectoryContents(path)
        }
    }

    toggleSidebar(): void {
        this.sidebarOpen = !this.sidebarOpen
    }

    setSidebarOpen(open: boolean): void {
        this.sidebarOpen = open
    }

    async loadDirectoryContents(dirPath: string): Promise<void> {
        if (this.loadingPaths.has(dirPath)) return
        if (this.directoryContents.has(dirPath)) return

        const revision = this.directoryRevision
        this.loadingPaths.add(dirPath)

        try {
            // Always request hidden files, we filter infra dirs (like .git) on frontend
            // The showHidden toggle controls whether user sees dotfiles like .env
            const result = await filesApi.describePath(
                this.withContext({
                    path: dirPath,
                    showHidden: true,
                })
            )
            runInAction(() => {
                if (revision !== this.directoryRevision) return
                if (result.type === "dir") {
                    // Filter out infrastructure directories (always hidden)
                    // and dotfiles if showHidden is false
                    const filtered = result.entries.filter((e) => {
                        // Always hide infra dirs like .git
                        if (e.isDir && HIDDEN_INFRA_DIRS.has(e.name)) return false
                        // Hide dotfiles unless showHidden is true
                        if (!this.showHidden && e.name.startsWith(".")) return false
                        return true
                    })
                    this.directoryContents.set(dirPath, filtered)
                }
                this.loadingPaths.delete(dirPath)
            })
        } catch {
            runInAction(() => {
                if (revision !== this.directoryRevision) return
                this.loadingPaths.delete(dirPath)
            })
        }
    }

    toggleExpanded(dirPath: string): void {
        if (this.expandedPaths.has(dirPath)) {
            this.expandedPaths.delete(dirPath)
        } else {
            this.expandedPaths.add(dirPath)
            // Load contents if not cached
            if (!this.directoryContents.has(dirPath)) {
                this.loadDirectoryContents(dirPath)
            }
        }
    }

    collapseAll(): void {
        // Keep only the root expanded
        this.expandedPaths.clear()
        if (this.workingDir) {
            this.expandedPaths.add(this.workingDir)
        }
    }

    private expandPath(absolutePath: string, includeSelf: boolean): void {
        if (!this.workingDir) return
        // The tree only contains the working directory, so a path outside it has
        // no row to reveal and must not queue reads for segments it does not own.
        if (this.isExternalPath(absolutePath)) return

        // Get all directories from repo root to the target path.
        const relativePath = getRelativePath(this.workingDir, absolutePath)
        const parts = splitPath(relativePath)
        const lastIndex = includeSelf ? parts.length : parts.length - 1

        let currentPath = this.workingDir
        this.expandedPaths.add(currentPath)
        if (!this.directoryContents.has(currentPath)) {
            this.loadDirectoryContents(currentPath)
        }
        const sep = getPathSeparator()
        for (let i = 0; i < lastIndex; i++) {
            currentPath = `${currentPath}${sep}${parts[i]}`
            this.expandedPaths.add(currentPath)
            if (!this.directoryContents.has(currentPath)) {
                this.loadDirectoryContents(currentPath)
            }
        }
    }

    expandPathToFile(filePath: string): void {
        this.expandPath(filePath, false)
    }

    expandPathToDirectory(dirPath: string): void {
        this.expandPath(dirPath, true)
    }

    setSearchQuery(query: string): void {
        this.searchQuery = query
        if (query) {
            this.performSearch(query)
        } else {
            this.searchResults = []
            this.untrackedSearchResults.clear()
        }
    }

    private performSearch(query: string): void {
        if (this.searchDebounceTimer) {
            clearTimeout(this.searchDebounceTimer)
        }

        this.searchDebounceTimer = setTimeout(async () => {
            const revision = this.directoryRevision
            runInAction(() => {
                this.searchLoading = true
            })
            try {
                const result = await filesApi.fuzzySearch(
                    this.withContext({
                        dir: this.workingDir,
                        query,
                        matchDirs: false,
                        limit: 50,
                    })
                )
                runInAction(() => {
                    if (revision !== this.directoryRevision) return
                    // Filter out paths inside infra dirs like .git/
                    const visibleResults = result.results.filter((path) => {
                        const firstSegment = splitPath(path)[0]
                        return !HIDDEN_INFRA_DIRS.has(firstSegment)
                    })
                    const untrackedResults = new Set(result.untrackedResults)
                    this.searchResults = visibleResults
                    this.untrackedSearchResults = new Set(visibleResults.filter((path) => untrackedResults.has(path)))
                    this.searchLoading = false
                })
            } catch {
                runInAction(() => {
                    if (revision !== this.directoryRevision) return
                    this.searchResults = []
                    this.untrackedSearchResults.clear()
                    this.searchLoading = false
                })
            }
        }, 150)
    }

    private resolveFileReferencePath(referencePath: string): string | null {
        const trimmed = normalizeFileReferenceInput(referencePath)
        if (!trimmed) return null
        if (isAbsolutePathReference(trimmed)) return trimmed

        const parts = splitPath(trimmed).filter((part) => part !== ".")
        if (parts.length === 0 || parts.some((part) => part === "..")) return null
        if (!this.workingDir) return null

        return joinPath(this.workingDir, parts.join(getPathSeparator()))
    }

    private fileReferenceSearchQuery(referencePath: string): string {
        const trimmed = normalizeFileReferenceInput(referencePath)
        if (!trimmed) return ""
        if (isAbsolutePathReference(trimmed)) {
            const relative = getRelativePath(this.workingDir, trimmed)
            if (relative !== trimmed) return relative
            return getFileName(trimmed)
        }
        return trimmed
    }

    private async describeAt(path: string, options: { readContents?: boolean; maxReadSize?: number } = {}): Promise<DescribePathResponse> {
        if (this.isExternalPath(path)) return filesApi.describeHostPath({ path, ...options })
        return filesApi.describePath(this.withContext({ path, ...options }))
    }

    private async fileExists(path: string, revision: number): Promise<boolean> {
        try {
            const result = await this.describeAt(path)
            return revision === this.directoryRevision && result.type === "file"
        } catch {
            return false
        }
    }

    private async findFuzzyPathReference(referencePath: string, matchDirs: boolean, revision: number): Promise<string | null> {
        if (!this.workingDir || revision !== this.directoryRevision) return null

        const workingDir = this.workingDir
        const query = this.fileReferenceSearchQuery(referencePath)
        if (!query) return null

        try {
            const result = await filesApi.fuzzySearch(
                this.withContext({
                    dir: workingDir,
                    query,
                    matchDirs,
                    limit: FILE_REFERENCE_SEARCH_LIMIT,
                })
            )
            if (revision !== this.directoryRevision) return null
            const match = chooseBestFuzzyReferenceMatch(result.results, query)
            return match ? joinPath(workingDir, match) : null
        } catch {
            return null
        }
    }

    private async describePath(path: string, revision: number): Promise<DescribePathResponse | null> {
        try {
            const result = await this.describeAt(path)
            return revision === this.directoryRevision ? result : null
        } catch {
            return null
        }
    }

    private async openResolvedPath(path: string, revision: number, options: { line?: number | null } = {}): Promise<boolean> {
        const result = await this.describePath(path, revision)
        if (revision !== this.directoryRevision) return false
        if (result?.type === "file") {
            await this.openFile(path, options)
            return true
        }
        if (result?.type === "dir") {
            this.focusDirectory(path)
            return true
        }
        return false
    }

    async openFileReference(referencePath: string, options: { line?: number | null } = {}): Promise<void> {
        const revision = this.directoryRevision
        const exactPath = this.resolveFileReferencePath(referencePath)
        if (exactPath && (await this.fileExists(exactPath, revision))) {
            await this.openFile(exactPath, options)
            return
        }
        if (revision !== this.directoryRevision) return
        // An absolute reference names one exact file. Searching the repo for a
        // same-named file would silently open different content.
        if (exactPath && isAbsolutePathReference(normalizeFileReferenceInput(referencePath))) {
            await this.openFile(exactPath, options)
            return
        }

        const fuzzyPath = await this.findFuzzyPathReference(referencePath, false, revision)
        if (fuzzyPath) {
            await this.openFile(fuzzyPath, options)
            return
        }
        if (revision !== this.directoryRevision) return

        if (exactPath) {
            await this.openFile(exactPath, options)
            return
        }

        this.setSearchQuery(this.fileReferenceSearchQuery(referencePath))
    }

    async openPathReference(referencePath: string, options: { line?: number | null } = {}): Promise<void> {
        const revision = this.directoryRevision
        const exactPath = this.resolveFileReferencePath(referencePath)
        if (exactPath && (await this.openResolvedPath(exactPath, revision, options))) return
        if (revision !== this.directoryRevision) return
        // Report the exact absolute reference as missing rather than
        // fuzzy-opening a repo file that merely shares its name.
        if (exactPath && isAbsolutePathReference(normalizeFileReferenceInput(referencePath))) {
            await this.openFile(exactPath, options)
            return
        }

        const fuzzyFilePath = await this.findFuzzyPathReference(referencePath, false, revision)
        if (fuzzyFilePath && (await this.openResolvedPath(fuzzyFilePath, revision, options))) return
        if (revision !== this.directoryRevision) return

        const fuzzyDirPath = await this.findFuzzyPathReference(referencePath, true, revision)
        if (fuzzyDirPath && (await this.openResolvedPath(fuzzyDirPath, revision, options))) return
        if (revision !== this.directoryRevision) return

        if (exactPath && options.line) {
            await this.openFile(exactPath, options)
        }
    }

    async openFile(absolutePath: string, options: { line?: number | null } = {}): Promise<void> {
        const revision = this.directoryRevision
        const line = options.line
        this.activeLine = typeof line === "number" && Number.isInteger(line) && line > 0 ? line : null

        // If opening from search, clear search so the tree can show the file.
        if (this.searchQuery) {
            this.searchQuery = ""
            this.searchResults = []
            this.untrackedSearchResults.clear()
        }

        // Expand parent directories to reveal the file in the sidebar.
        this.expandPathToFile(absolutePath)

        // Add to tabs.
        this.addToTabs(absolutePath)
        this.activeFile = absolutePath
        this.selectedPath = absolutePath
        this.fileLoading = true
        this.activeFileData = null
        this.fileError = null

        try {
            // Image bytes stream over HTTP, so an inline read would be
            // transferred and discarded. Text still describes in one call.
            const contentRead = { readContents: true, maxReadSize: MAX_FILE_READ_SIZE }
            const defersContentRead = hasImageExtension(absolutePath) || hasPdfExtension(absolutePath)
            const described = await this.describeAt(absolutePath, defersContentRead ? {} : contentRead)
            const result =
                defersContentRead && described.type === "file" && !described.previewKind && !described.isBinary && described.content === null
                    ? await this.describeAt(absolutePath, contentRead)
                    : described
            runInAction(() => {
                if (revision !== this.directoryRevision || this.activeFile !== absolutePath) return
                if (result.type === "file") {
                    this.activeFileData = result
                    this.fileError = null
                } else if (result.type === "not_found") {
                    this.fileError = "File not found"
                } else if (result.type === "error") {
                    this.fileError = result.message
                } else {
                    this.fileError = "Not a file"
                }
                this.fileLoading = false
            })
        } catch (err) {
            runInAction(() => {
                if (revision !== this.directoryRevision || this.activeFile !== absolutePath) return
                this.fileError = err instanceof Error ? err.message : "Failed to load file"
                this.fileLoading = false
            })
        }
    }

    focusDirectory(absolutePath: string): void {
        if (this.searchQuery) {
            this.searchQuery = ""
            this.searchResults = []
            this.untrackedSearchResults.clear()
        }
        this.activeLine = null
        this.expandPathToDirectory(absolutePath)
        this.selectedPath = absolutePath
    }

    private addToTabs(path: string): void {
        const name = getFileName(path)

        if (this.openTabs.some((t) => t.path === path)) return

        // Add to end
        this.openTabs.push({ path, name, external: this.isExternalPath(path) })

        // Trim to max size (remove oldest)
        while (this.openTabs.length > MAX_OPEN_TABS) {
            this.openTabs.shift()
        }
    }

    closeTab(path: string): void {
        const index = this.openTabs.findIndex((t) => t.path === path)
        if (index === -1) return

        this.openTabs.splice(index, 1)

        // If closing active file, switch to another tab or clear
        if (this.activeFile === path) {
            if (this.openTabs.length > 0) {
                // Switch to the tab that was next, or the last one
                const newIndex = Math.min(index, this.openTabs.length - 1)
                this.openFile(this.openTabs[newIndex].path)
            } else {
                this.activeFile = null
                this.activeFileData = null
                this.activeLine = null
                this.fileError = null
            }
        }
    }

    switchToTab(path: string): void {
        const tab = this.openTabs.find((t) => t.path === path)
        if (tab) {
            this.openFile(path)
        }
    }

    setShowHidden(show: boolean): void {
        this.showHidden = show
        // Clear cache and reload expanded directories
        this.directoryContents.clear()
        for (const path of this.expandedPaths) {
            this.loadDirectoryContents(path)
        }
    }

    selectPath(path: string): void {
        this.selectedPath = path
    }

    refreshTree(): void {
        console.debug("[FileBrowserManager] refreshTree called", { activeFile: this.activeFile, expandedPaths: [...this.expandedPaths] })
        this.directoryContents.clear()
        for (const dirPath of this.expandedPaths) {
            this.loadDirectoryContents(dirPath)
        }
        // Reload active file contents if one is open
        if (this.activeFile) {
            console.debug("[FileBrowserManager] refreshTree: reloading active file", this.activeFile)
            this.openFile(this.activeFile, { line: this.activeLine })
        }
    }

    get isSearching(): boolean {
        return this.searchQuery.length > 0
    }

    get flattenedTree(): TreeNode[] {
        if (!this.workingDir) return []

        const result: TreeNode[] = []

        const traverse = (dirPath: string, depth: number) => {
            const entries = this.directoryContents.get(dirPath) || []

            for (const entry of entries) {
                const isExpanded = this.expandedPaths.has(entry.path)
                const isLoading = this.loadingPaths.has(entry.path)

                result.push({
                    path: entry.path,
                    name: entry.name,
                    isDir: entry.isDir,
                    depth,
                    isExpanded,
                    isLoading,
                })

                // Recurse into expanded directories
                if (entry.isDir && isExpanded) {
                    traverse(entry.path, depth + 1)
                }
            }
        }

        // Start from root
        traverse(this.workingDir, 0)

        return result
    }

    get searchDisplayItems(): Array<{ name: string; fullPath: string; untracked: boolean }> {
        return this.searchResults.map((relativePath) => ({
            name: relativePath,
            fullPath: joinPath(this.workingDir, relativePath),
            untracked: this.untrackedSearchResults.has(relativePath),
        }))
    }

    closeFile(): void {
        if (this.activeFile) {
            this.closeTab(this.activeFile)
        }
    }
}
